import React, { useState, useEffect } from 'react';
import { Target, Calendar, CheckCircle, ArrowRight, Star, Code, TrendingUp } from 'lucide-react';
import { GradExpectation } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

export default function GradExpectationsPage() {
  const [expectations, setExpectations] = useState<GradExpectation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  useEffect(() => {
    fetchExpectations();
  }, []);

  const fetchExpectations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/grad-expectations`);
      if (!response.ok) {
        throw new Error('Failed to fetch GRAD expectations');
      }
      const data = await response.json();
      setExpectations(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching expectations:', err);
    } finally {
      setLoading(false);
    }
  };

  const getExpectationIcon = (type?: string) => {
    switch (type) {
      case 'goals': return <Target className="h-5 w-5" />;
      case 'impact': return <TrendingUp className="h-5 w-5" />;
      case 'leadership': return <Star className="h-5 w-5" />;
      case 'overall': return <CheckCircle className="h-5 w-5" />;
      default: return <Target className="h-5 w-5" />;
    }
  };

  const getExpectationColor = (type?: string) => {
    switch (type) {
      case 'goals': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'impact': return 'bg-green-100 text-green-800 border-green-200';
      case 'leadership': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'overall': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredExpectations = selectedYear 
    ? expectations.filter(exp => exp.year === selectedYear)
    : expectations;

  const years = Array.from(new Set(expectations.map(exp => exp.year))).sort((a, b) => b - a);

  const groupedExpectations = filteredExpectations.reduce((acc, expectation) => {
    const key = expectation.year;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(expectation);
    return acc;
  }, {} as Record<number, GradExpectation[]>);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading GRAD expectations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Expectations</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchExpectations}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Target className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">GRAD Expectations</h1>
                <p className="text-sm text-gray-600">Performance expectations and goals</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <div className="text-sm text-gray-500">
                {filteredExpectations.length} expectations
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expectations by Year */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {Object.entries(groupedExpectations).map(([year, yearExpectations]) => (
            <div key={year} className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  {year} GRAD Expectations
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {yearExpectations.length} expectation{yearExpectations.length !== 1 ? 's' : ''}
                </p>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  {yearExpectations.map((expectation) => (
                    <div key={expectation.expectation_id} className="border-l-4 border-blue-200 pl-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {getExpectationIcon(expectation.expectation_type)}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExpectationColor(expectation.expectation_type)}`}>
                            {expectation.expectation_type || 'General'}
                          </span>
                        </div>
                        {expectation.job_level && (
                          <span className="text-sm text-gray-500">L{expectation.job_level}</span>
                        )}
                        {expectation.job_family && (
                          <span className="text-sm text-gray-500 ml-2">({expectation.job_family})</span>
                        )}
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed">
                        {expectation.expectation_text}
                      </p>
                      
                      {expectation.quarter && (
                        <div className="mt-2 text-sm text-gray-500">
                          Quarter {expectation.quarter}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredExpectations.length === 0 && (
          <div className="text-center py-12">
            <Target className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No expectations found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedYear 
                ? `No GRAD expectations found for ${selectedYear}.`
                : 'No GRAD expectations have been recorded yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Key Focus Areas */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Key Focus Areas</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Code className="h-6 w-6 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Technical Development</h3>
              </div>
              <p className="text-sm text-gray-700">
                Focus on building technical skills and coding capabilities despite role restrictions.
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <TrendingUp className="h-6 w-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Impact Delivery</h3>
              </div>
              <p className="text-sm text-gray-700">
                Deliver measurable impact through program management and process optimization.
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <div className="flex items-center mb-3">
                <Star className="h-6 w-6 text-purple-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Leadership</h3>
              </div>
              <p className="text-sm text-gray-700">
                Demonstrate leadership through cross-functional collaboration and team building.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Context */}
      <div className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Performance Context</h2>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">2024 Challenges</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">Role transition to non-coding position</p>
                  </div>
                  <div className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">Manager expectations for technical progress</p>
                  </div>
                  <div className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">Ethical conflicts with data manipulation</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">2025 Goals</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">Unified metrics platform development</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">AI-powered solution implementation</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">Technical solution maintenance and enhancement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
